import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Wrench, Hammer, Crown, GraduationCap, Rocket, Scale, Search, Compass, Users, Library as LibraryIcon } from "lucide-react";
import {
  useUserPreferences,
  type SkillLevel,
  type PrimaryGoal,
} from "@/hooks/useUserPreferences";
import { toast } from "sonner";

/**
 * Three-step welcome flow shown once per account, right after signup.
 * Step 1: skill level (curious / tinkerer / builder / pro)
 * Step 2: primary goal (learn / build / compare / browse)
 * Step 3: drops the user on the page that matches their goal.
 *
 * Skippable at any step — skipping still marks `onboarded_at` so we don't
 * pester the user on every page load.
 */
const LEVELS: Array<{
  value: SkillLevel;
  label: string;
  blurb: string;
  Icon: typeof Sparkles;
}> = [
  {
    value: "curious",
    label: "Curious",
    blurb: "I've heard about ChatGPT but haven't used much.",
    Icon: Sparkles,
  },
  {
    value: "tinkerer",
    label: "Tinkerer",
    blurb: "I use a couple of AI tools weekly.",
    Icon: Wrench,
  },
  {
    value: "builder",
    label: "Builder",
    blurb: "I write prompts, chain tools, ship workflows.",
    Icon: Hammer,
  },
  {
    value: "pro",
    label: "Pro",
    blurb: "I work with APIs, agents, fine-tuning daily.",
    Icon: Crown,
  },
];

const GOALS: Array<{
  value: PrimaryGoal;
  label: string;
  blurb: string;
  route: string;
  Icon: typeof Sparkles;
}> = [
  {
    value: "learn",
    label: "Learn",
    blurb: "Guides, protocols, AI fundamentals.",
    route: "/learn",
    Icon: GraduationCap,
  },
  {
    value: "build",
    label: "Build with AI",
    blurb: "Use Chat, Image, Research right here.",
    route: "/workspace",
    Icon: Rocket,
  },
  {
    value: "compare",
    label: "Compare tools",
    blurb: "Find the best AI for the job.",
    route: "/compare",
    Icon: Scale,
  },
  {
    value: "browse",
    label: "Just browse",
    blurb: "Explore the 5,000+ tool directory.",
    route: "/directory",
    Icon: Search,
  },
];

export function OnboardingModal() {
  const { needsOnboarding, save, isSaving } = useUserPreferences();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [level, setLevel] = useState<SkillLevel | null>(null);
  const [chosenGoal, setChosenGoal] = useState<PrimaryGoal | null>(null);

  const open = needsOnboarding;

  const finish = async (goal: PrimaryGoal | null) => {
    try {
      await save({
        skill_level: level ?? "curious",
        primary_goal: goal,
        onboarded_at: new Date().toISOString(),
      });
      const route = goal ? GOALS.find((g) => g.value === goal)?.route : null;
      if (route) navigate(route);
      toast.success("You're in. Game's tuned to your level.");
    } catch (err) {
      console.error("[onboarding] save failed", err);
      toast.error("Couldn't save your prefs. Tweak them anytime in Settings.");
    }
  };

  const skip = async () => {
    // Still stamp onboarded_at so we don't keep nagging them.
    try {
      await save({
        skill_level: level ?? "curious",
        primary_goal: null,
        onboarded_at: new Date().toISOString(),
      });
    } catch {
      /* ignore — worst case modal reappears next session */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Treat dismissal as skip so we don't loop.
        if (!o) void skip();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {step === 1
              ? "Welcome to the game."
              : step === 2
              ? "What's your first move?"
              : "What you unlock by playing."}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Two quick questions. Then we tune the game to you."
              : step === 2
              ? "Pick a first move — we'll spawn you on the right page."
              : "These three live in your account menu, ready when you are."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {LEVELS.map(({ value, label, blurb, Icon }) => {
              const selected = level === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setLevel(value)}
                  className={`text-left rounded-lg border p-4 transition-colors ${
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="font-semibold">{label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{blurb}</p>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {GOALS.map(({ value, label, blurb, Icon }) => (
              <button
                key={value}
                type="button"
                disabled={isSaving}
                onClick={() => {
                  setChosenGoal(value);
                  setStep(3);
                }}
                className="text-left rounded-lg border border-border p-4 transition-colors hover:bg-muted/50 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4 text-primary" />
                  <span className="font-semibold">{label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{blurb}</p>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="mt-2 space-y-3">
            {[
              {
                Icon: Compass,
                title: "Mastery",
                blurb:
                  "Your points, tier, and the next 3 moves to level up. Earn certifications you can post on LinkedIn.",
              },
              {
                Icon: Users,
                title: "Squads",
                blurb:
                  "Team up with other hackers. Run protocols together, climb the weekly leaderboard.",
              },
              {
                Icon: LibraryIcon,
                title: "Master Library",
                blurb:
                  "Community-built playbooks. Fork what works, publish your own, claim open bounties for bonus points.",
              },
            ].map(({ Icon, title, blurb }) => (
              <div
                key={title}
                className="flex gap-3 rounded-lg border border-border p-3"
              >
                <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">{title}</div>
                  <p className="text-xs text-muted-foreground">{blurb}</p>
                </div>
              </div>
            ))}
            <p className="text-[11px] font-mono text-muted-foreground text-center pt-1">
              Find them anytime under your name → <span className="text-primary">Your journey</span>.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mt-2 pt-4 border-t">
          <Button variant="ghost" size="sm" onClick={skip} disabled={isSaving}>
            Skip for now
          </Button>
          <div className="flex items-center gap-2">
            {(step === 2 || step === 3) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep((step - 1) as 1 | 2)}
                disabled={isSaving}
              >
                Back
              </Button>
            )}
            {step === 1 && (
              <Button
                size="sm"
                onClick={() => setStep(2)}
                disabled={!level || isSaving}
              >
                Next
              </Button>
            )}
            {step === 3 && (
              <Button
                size="sm"
                onClick={() => finish(chosenGoal)}
                disabled={isSaving}
              >
                Let's go
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
